/**
 * Друкована форма реалізації: «Видаткова накладна» (товари) або «Акт виконаних робіт
 * (наданих послуг)» (?form=act чи документ лише з послуг).
 *
 * ч.1374 (рішення власника): таблична частина, підсумки і сума прописом — за зразком 1С
 * (`PrintLines1c`): № | Товари (роботи, послуги) | Кіл-сть | Од. | Ціна … | Сума …; заголовки
 * й підсумки — за ознаками ПДВ документа («Враховувати ПДВ», «Сума включає ПДВ»). У шапці —
 * усе заповнене: договір, рахунок-підстава, склад, адреса доставки.
 */
import { useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { goodsSales } from '@/api/goodsSales';
import { customerOrders } from '@/api/orders';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { assetsRegistry } from '@/api/assetsRegistry';
import { refs } from '@/api/refs';
import PrintActions from '@/shared/components/PrintActions';
import PrintLines1c, { type PrintLine } from '@/shared/components/PrintLines1c';
import { partyCodesText } from '@/shared/utils/partyCodes';
import { contractLabel } from '@/shared/utils/labels';
import { printLineAmount, vatMode } from '@/shared/utils/vatFlags';

const MONTHS_UK = [
  'січня', 'лютого', 'березня', 'квітня', 'травня', 'червня',
  'липня', 'серпня', 'вересня', 'жовтня', 'листопада', 'грудня',
];

function formatDateUk(iso: string): string {
  const d = new Date(iso);
  return `« ${d.getDate()} » ${MONTHS_UK[d.getMonth()]!} ${d.getFullYear()} р.`;
}

export default function GoodsSalePrintPage() {
  const { id } = useParams();
  // 2026-07-26: ?form=act — друк АКТА наданих послуг зі змішаного документа.
  const [searchParams] = useSearchParams();
  const wantAct = searchParams.get('form') === 'act';
  const docQ = useQuery({
    queryKey: ['goods-sale', id],
    queryFn: () => goodsSales.get(id!),
    enabled: !!id,
  });
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const nomQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.listAll(),
  });
  const uomsQ = useQuery({ queryKey: ['uoms'], queryFn: refs.uoms });
  const warehousesQ = useQuery({
    queryKey: ['warehouses', docQ.data?.organization_id],
    queryFn: () => refs.warehouses(docQ.data!.organization_id),
    enabled: !!docQ.data,
  });
  const contractsQ = useQuery({
    queryKey: ['contracts', docQ.data?.organization_id, docQ.data?.contractor_id],
    queryFn: () => refs.contracts({
      organization_id: docQ.data!.organization_id, contractor_id: docQ.data!.contractor_id,
    }),
    enabled: !!docQ.data,
  });
  // Рахунок/замовлення-підстава — у друк як «Підстава».
  const orderQ = useQuery({
    queryKey: ['customer-order', docQ.data?.customer_order_id],
    queryFn: () => customerOrders.get(docQ.data!.customer_order_id!),
    enabled: !!docQ.data?.customer_order_id,
  });
  // 0445: рядки-ОЗ — назва з реєстру активів (номенклатури немає).
  const faQ = useQuery({
    queryKey: ['fa-for-sale', docQ.data?.organization_id],
    queryFn: () => assetsRegistry.list({ organization_id: docQ.data!.organization_id, kind: 'FIXED_ASSET', limit: 500 }),
    enabled: !!docQ.data && docQ.data.items.some((it) => it.fixed_asset_id),
  });
  const faName = (id?: string | null) => {
    const a = id ? faQ.data?.items.find((x) => x.id === id) : undefined;
    return a ? `${a.name} (інв. № ${a.inventory_number})` : undefined;
  };

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  const nomMap = useMemo(() => {
    const m = new Map<string, { code: string | null; name: string; is_service: boolean; base_uom_id: string | null }>();
    nomQ.data?.items.forEach((n) => m.set(n.id, {
      code: n.code, name: n.name, is_service: n.is_service, base_uom_id: n.base_uom_id ?? null,
    }));
    return m;
  }, [nomQ.data]);
  const uomById = useMemo(() => new Map((uomsQ.data ?? []).map((u) => [u.id, u.short_name])), [uomsQ.data]);

  if (!docQ.data || !orgsQ.data || !contractorsQ.data || !nomQ.data) {
    return <div className="card"><p>Завантаження…</p></div>;
  }

  const d = docQ.data;
  const org = orgsQ.data.items.find((o) => o.id === d.organization_id);
  const buyer = contractorsQ.data.items.find((c) => c.id === d.contractor_id);
  const wh = warehousesQ.data?.find((w) => w.id === d.warehouse_id);
  const contract = contractsQ.data?.find((c) => c.id === d.contract_id);
  // ч.661: склад рядка — джерело істини. Якщо у товарних рядках >1 складу —
  // друкуємо КОЛОНКУ «Склад», інакше — рядок шапки як раніше.
  const whById = new Map((warehousesQ.data ?? []).map((w) => [w.id, w]));
  const effWhIds = new Set(
    d.items
      .filter((it) => it.nomenclature_id && !nomMap.get(it.nomenclature_id)?.is_service)
      .map((it) => (it as { warehouse_id?: string | null }).warehouse_id || d.warehouse_id || '')
      .filter(Boolean));
  const multiWarehouse = effWhIds.size > 1;
  const singleWh = !multiWarehouse && effWhIds.size === 1
    ? whById.get([...effWhIds][0]!) ?? wh : wh;

  if (!org || !buyer) {
    return <div className="card"><p>Не знайдено організацію або покупця.</p></div>;
  }

  // Тип документа: ?form=act → акт зі строк-послуг; всі рядки послуги →
  // акт (все); інакше — видаткова накладна зі строк-товарів.
  const allServices = d.items.length > 0 && d.items.every(
    (it) => nomMap.get(it.nomenclature_id ?? '')?.is_service === true,
  );
  const printItems = wantAct
    ? (allServices ? d.items
       : d.items.filter((it) => nomMap.get(it.nomenclature_id ?? '')?.is_service))
    : (allServices ? d.items
       : d.items.filter((it) => !nomMap.get(it.nomenclature_id ?? '')?.is_service));
  const mode = vatMode(d, org.is_vat_payer);
  const net = printItems.reduce((a, it) => a + Number(it.amount_without_vat), 0);
  const vat = mode === 'none' ? 0 : printItems.reduce((a, it) => a + Number(it.vat_amount), 0);
  const lines: PrintLine[] = printItems.map((it) => {
    const nom = nomMap.get(it.nomenclature_id ?? '');
    const w = whById.get((it as { warehouse_id?: string | null }).warehouse_id || d.warehouse_id || '');
    return {
      name: `${nom?.name ?? faName(it.fixed_asset_id) ?? '—'}${it.service_description ? ` — ${it.service_description}` : ''}`,
      qty: Number(it.quantity),
      uom: (nom?.base_uom_id && uomById.get(nom.base_uom_id)) || (nom?.is_service ? 'посл.' : 'шт'),
      price: Number(it.price),
      amount: printLineAmount(it, mode),
      discount: Number((it as { discount_percent?: string | null }).discount_percent ?? 0) || null,
      extra: multiWarehouse ? { wh: w ? (w.code || w.name) : '—' } : undefined,
    };
  });
  const asAct = wantAct || allServices;
  const docTitle = asAct
    ? 'Акт виконаних робіт (наданих послуг)'
    : 'Видаткова накладна';
  const nameHeader = asAct ? 'Найменування робіт (послуг)' : 'Товари (роботи, послуги)';
  const supplierSignLabel = asAct ? 'Виконавець (підпис, П.І.Б.)' : 'Відпустив (підпис, П.І.Б.)';
  const buyerSignLabel = asAct ? 'Замовник (підпис, П.І.Б.)' : 'Отримав (підпис, П.І.Б.)';
  // Представлення договору — з картки договору (print_deal_info / print_name).
  const contractLine = contract && contract.print_deal_info !== false
    ? (contract.print_name || contractLabel(contract)) : null;
  const requisites: Array<[string, string]> = [];
  if (contractLine) requisites.push(['Договір', contractLine]);
  if (orderQ.data) {
    requisites.push(['Підстава', `${orderQ.data.order_kind === 'PROFORMA_INVOICE' ? 'рахунок' : 'замовлення'} `
      + `№ ${orderQ.data.document_number} від ${new Date(orderQ.data.document_date).toLocaleDateString('uk-UA')}`]);
  }
  if (singleWh && !allServices && !multiWarehouse) requisites.push(['Склад відвантаження', `${singleWh.name} — ${singleWh.code}`]);
  if (d.delivery_address) requisites.push(['Адреса доставки', d.delivery_address]);

  return (
    <div className="print-page invoice-print">
      <style>{`
        .invoice-print {
          background: white; color: black; max-width: 900px; margin: 0 auto;
          padding: 1.5rem;
          font-family: 'Times New Roman', serif; font-size: 10.5pt; line-height: 1.3;
        }
        .invoice-print h1 { font-size: 13pt; text-align: center; margin: 0; }
        .invoice-print .center { text-align: center; }
        .invoice-print .small { font-size: 9pt; color: #555; }
        .invoice-print .header { margin-bottom: 0.75rem; }
        .invoice-print .party { margin: 0.35rem 0; }
        .invoice-print .party .label { font-size: 9pt; color: #555; }
        .invoice-print .signs { margin-top: 1.5rem; display: grid; grid-template-columns: 1fr 1fr; gap: 2rem; }
        .invoice-print .sign { border-bottom: 1px solid black; min-height: 22px; }
        .invoice-print .sign-label { font-size: 9pt; color: #555; margin-top: 4px; }
        .invoice-print .actions { display: flex; gap: 0.5rem; margin-bottom: 1rem; }
        @media print {
          .invoice-print .actions { display: none; }
          .invoice-print { padding: 0; max-width: none; }
          body { background: white !important; }
        }
      `}</style>

      <PrintActions docMeta={{
        organizationId: docQ.data?.organization_id ?? '',
        documentType: 'doc.goods_sales',
        documentId: docQ.data?.id ?? '',
        filename: `${asAct ? 'Акт' : 'Видаткова'} ${docQ.data?.document_number ?? ''}`,
        orgName: org.name,
        contractorEmail: buyer.email ?? null,
      }} />

      <div className="header">
        <h1>{docTitle} № {d.document_number}</h1>
        <p className="center" style={{ margin: '0.25rem 0' }}>від {formatDateUk(d.document_date)}</p>
      </div>

      <div className="party">
        <span className="label">Постачальник: </span>
        <strong>{org.full_name || org.name}</strong>
        {partyCodesText(org) && <span>, {partyCodesText(org)}</span>}
        {mode === 'none' && !org.is_vat_payer && <span>, неплатник ПДВ</span>}
        {org.legal_address && <span>, {org.legal_address}</span>}
      </div>

      <div className="party">
        <span className="label">{asAct ? 'Замовник: ' : 'Одержувач: '}</span>
        <strong>{buyer.full_name || buyer.name}</strong>
        {partyCodesText(buyer) && <span>, {partyCodesText(buyer)}</span>}
        {buyer.legal_address && <span>, {buyer.legal_address}</span>}
      </div>

      {requisites.map(([label, value]) => (
        <div className="party" key={label}>
          <span className="label">{label}: </span>{value}
        </div>
      ))}

      <PrintLines1c mode={mode} lines={lines} net={net} vat={vat} nameHeader={nameHeader}
        extraColumns={multiWarehouse ? [{ key: 'wh', header: 'Склад', width: '10%' }] : []} />

      <div className="signs">
        <div>
          <div className="sign">&nbsp;</div>
          <div className="sign-label">{supplierSignLabel}</div>
        </div>
        <div>
          <div className="sign">&nbsp;</div>
          <div className="sign-label">{buyerSignLabel}</div>
        </div>
      </div>

      <p className="small" style={{ marginTop: '1rem' }}>
        М.П. Постачальника &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        М.П. {asAct ? 'Замовника' : 'Одержувача'}
      </p>
    </div>
  );
}
